import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  FolderPlus, Folder, FolderOpen, FileText, Upload, Search, Loader2,
  Trash2, Download, Pencil, ChevronRight, ChevronDown, Home,
  Users, Cloud, CheckCircle2, RefreshCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";

const SCOPE_META = {
  personal: { label: "Mes Documents (RH)", color: "#8b5cf6", icon: Users },
  client: { label: "Clients Blitzz", color: "#3b82f6", icon: Users },
  general: { label: "General", color: "#f7c935", icon: Folder },
};

function ScopeBadge({ scope }) {
  const meta = SCOPE_META[scope] || SCOPE_META.general;
  return (
    <span
      className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold"
      style={{ background: meta.color + "20", color: meta.color }}
    >
      {scope}
    </span>
  );
}

function FolderRow({ node, depth, selected, onSelect, onToggle, expanded }) {
  const hasChildren = (node.children || []).length > 0;
  const isOpen = expanded.has(node.folder_id);
  const isActive = selected === node.folder_id;
  const color = SCOPE_META[node.scope] ? SCOPE_META[node.scope].color : "#f7c935";
  let chevron = <span className="w-3.5 h-3.5" />;
  if (hasChildren && isOpen) chevron = <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 text-slate-500" />;
  else if (hasChildren) chevron = <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-slate-500" />;
  const FolderIcon = (isActive || isOpen) ? FolderOpen : Folder;
  const cls = "w-full flex items-center gap-1.5 py-1.5 pr-2 text-left text-sm transition-colors rounded " +
    (isActive ? "bg-[#f7c935]/15 text-[#f7c935]" : "text-slate-300 hover:bg-white/5");
  return (
    <button
      onClick={() => { onSelect(node); if (hasChildren) onToggle(node.folder_id); }}
      className={cls}
      style={{ paddingLeft: (depth * 14 + 8) + "px" }}
      data-testid={"cloud-folder-" + node.folder_id}
    >
      {chevron}
      <FolderIcon className="w-4 h-4 flex-shrink-0" style={{ color }} />
      <span className="truncate flex-1">{node.name}</span>
      {node.doc_count > 0 && <span className="text-[9px] text-slate-500">{node.doc_count}</span>}
    </button>
  );
}

function flattenTree(nodes, expanded) {
  const out = [];
  const walk = (arr, depth) => {
    for (let i = 0; i < arr.length; i++) {
      const n = arr[i];
      out.push({ node: n, depth });
      if (n.children && n.children.length > 0 && expanded.has(n.folder_id)) {
        walk(n.children, depth + 1);
      }
    }
  };
  walk(nodes, 0);
  return out;
}

function UploadDialog({ onClose, onDone, api, team, defaultScope, defaultFolder }) {
  const [scope, setScope] = useState(defaultFolder ? defaultFolder.scope : (defaultScope || "general"));
  const [folderId, setFolderId] = useState(defaultFolder ? defaultFolder.folder_id : "");
  const [targetUserId, setTargetUserId] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [folderOptions, setFolderOptions] = useState([]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const r = await api.get("/cloud/folders/tree?scope=" + scope);
        const out = [];
        const walk = (nodes, depth) => {
          for (let i = 0; i < nodes.length; i++) {
            const n = nodes[i];
            out.push({ id: n.folder_id, label: "  ".repeat(depth) + n.name });
            walk(n.children || [], depth + 1);
          }
        };
        walk(r.data || [], 0);
        setFolderOptions(out);
      } catch (e) { /* ignore */ }
    };
    loadOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const doUpload = async () => {
    if (!file) return toast.error("Selectionnez un fichier");
    if (!folderId) return toast.error("Selectionnez un dossier");
    if (scope === "personal" && !targetUserId) return toast.error("Selectionnez un membre cible");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("scope", scope);
      fd.append("folder_id", folderId);
      if (scope === "personal") fd.append("target_user_id", targetUserId);
      if (title) fd.append("title", title);
      fd.append("file", file);
      await api.post("/cloud/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success("Document uploade");
      onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setUploading(false);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md bg-[#0b0811] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Uploader un document</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-slate-500 block mb-1">Portee</label>
            <select
              value={scope}
              onChange={(e) => { setScope(e.target.value); setFolderId(""); }}
              className={inputCls + " px-3 py-2 rounded w-full"}
            >
              <option value="personal">RH / Mes Documents</option>
              <option value="client">Clients</option>
              <option value="general">General</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 block mb-1">Dossier *</label>
            <select
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              className={inputCls + " px-3 py-2 rounded w-full"}
              data-testid="cloud-upload-folder"
            >
              <option value="">— Selectionner un dossier —</option>
              {folderOptions.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </div>
          {scope === "personal" && (
            <div>
              <label className="text-[10px] text-slate-500 block mb-1">Destinataire *</label>
              <select
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                className={inputCls + " px-3 py-2 rounded w-full"}
                data-testid="cloud-upload-target"
              >
                <option value="">— Membre de l'equipe —</option>
                {team.map((t) => (
                  <option key={t.user_id} value={t.user_id}>{t.name || t.email}</option>
                ))}
              </select>
            </div>
          )}
          <Input placeholder="Titre (optionnel)" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
          <div>
            <label className="text-[10px] text-slate-500 block mb-1">Fichier *</label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}
              className="block w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-[#f7c935] file:text-[#14101c] file:font-bold hover:file:bg-[#ffd948]"
              data-testid="cloud-upload-file"
            />
            <p className="text-[10px] text-slate-500 mt-1">Max 50 Mo. PDF, images, Office, CSV.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button
            onClick={doUpload}
            disabled={uploading || !file || !folderId}
            className="bg-[#f7c935] text-[#14101c] font-bold"
            data-testid="cloud-upload-submit"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Uploader
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FolderDialog({ folder, form, setForm, onClose, onSave }) {
  const isEditing = !!folder;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md bg-[#0b0811] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Renommer le dossier" : "Nouveau dossier"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Nom du dossier *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputCls}
            data-testid="cloud-folder-name"
            autoFocus
          />
          <Textarea
            placeholder="Description (optionnel)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            className={inputCls}
          />
          {!isEditing && (
            <div>
              <label className="text-[10px] text-slate-500 block mb-1">Portee</label>
              <select
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
                className={inputCls + " px-3 py-2 rounded w-full"}
                disabled={!!form.parent_id}
              >
                <option value="personal">RH / Mes Documents</option>
                <option value="client">Clients</option>
                <option value="general">General</option>
              </select>
              {form.parent_id && (
                <p className="text-[10px] text-slate-500 mt-1">Sous-dossier — portee heritee du parent</p>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button
            onClick={onSave}
            disabled={!form.name.trim()}
            className="bg-[#f7c935] text-[#14101c] font-bold"
            data-testid="cloud-folder-save"
          >
            {isEditing ? "Enregistrer" : "Creer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CloudDocumentsPage({ initialScope = "all", hideScopeTabs = false }) {
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const [activeScope, setActiveScope] = useState(initialScope);
  const [tree, setTree] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [folderForm, setFolderForm] = useState({ name: "", description: "", scope: "general", parent_id: null });
  const [editingFolder, setEditingFolder] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [team, setTeam] = useState([]);

  const isAdmin = user && (user.role === "admin" || user.role === "tenant_admin");

  const loadTree = useCallback(async () => {
    try {
      const scopeFilter = activeScope !== "all" ? "?scope=" + activeScope : "";
      const tRes = await api.get("/cloud/folders/tree" + scopeFilter);
      const sRes = await api.get("/cloud/stats");
      setTree(tRes.data || []);
      setStats(sRes.data);
    } catch (e) { console.error(e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScope]);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeScope !== "all") params.set("scope", activeScope);
      if (selectedFolder) params.set("folder_id", selectedFolder.folder_id);
      if (search) params.set("search", search);
      const r = await api.get("/cloud/documents?" + params.toString());
      setDocuments(r.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScope, selectedFolder, search]);

  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => {
    if (isAdmin) api.get("/cloud/team").then((r) => setTeam(r.data || [])).catch(() => {});
  // eslint-disable-next-line
  }, []);

  const openCreateFolder = (parent) => {
    setEditingFolder(null);
    setFolderForm({
      name: "",
      description: "",
      scope: parent ? parent.scope : (activeScope === "all" ? "general" : activeScope),
      parent_id: parent ? parent.folder_id : null,
    });
    setShowFolderDialog(true);
  };

  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setFolderForm({ name: folder.name, description: folder.description || "", scope: folder.scope, parent_id: folder.parent_id });
    setShowFolderDialog(true);
  };

  const saveFolder = async () => {
    if (!folderForm.name.trim()) return toast.error("Le nom est requis");
    try {
      if (editingFolder) {
        await api.put("/cloud/folders/" + editingFolder.folder_id, { name: folderForm.name, description: folderForm.description });
      } else {
        await api.post("/cloud/folders", folderForm);
      }
      setShowFolderDialog(false);
      toast.success(editingFolder ? "Dossier mis a jour" : "Dossier cree");
      loadTree();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm("Supprimer \"" + folder.name + "\" et tout son contenu ?")) return;
    try {
      await api.delete("/cloud/folders/" + folder.folder_id);
      toast.success("Dossier supprime");
      if (selectedFolder && selectedFolder.folder_id === folder.folder_id) setSelectedFolder(null);
      loadTree();
      loadDocs();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const toggleExpanded = (fid) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(fid)) n.delete(fid); else n.add(fid);
      return n;
    });
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm("Supprimer \"" + doc.title + "\" ?")) return;
    try {
      await api.delete("/cloud/documents/" + doc.doc_id);
      toast.success("Document supprime");
      loadDocs();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await api.get("/cloud/documents/" + doc.doc_id + "/download");
      if (r.data && r.data.url) {
        window.open(r.data.url, "_blank");
      } else if (r.data && r.data.content_b64) {
        const blob = await (await fetch("data:" + doc.content_type + ";base64," + r.data.content_b64)).blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.filename || doc.title || "document";
        a.click();
        URL.revokeObjectURL(url);
      } else if (r.data && r.data.data) {
        const blob = new Blob([JSON.stringify(r.data.data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = (doc.title || "document") + ".json";
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const scopeTabs = [
    { key: "all", label: "Tout le cloud", color: "#f7c935" },
    { key: "personal", label: "RH / Mes Documents", color: "#8b5cf6" },
    { key: "client", label: "Clients", color: "#3b82f6" },
    { key: "general", label: "General", color: "#10b981" },
  ];

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="cloud-documents-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#f7c935] font-semibold mb-2">
            <Cloud className="w-3.5 h-3.5" /> Documents Cloud
          </div>
          <h1 className="text-4xl font-black tracking-tighter leading-none">
            Votre <span style={{ color: "#f7c935" }}>cloud</span> documentaire
          </h1>
          <p className="text-slate-400 mt-2 text-sm max-w-2xl">
            Un seul endroit pour tous vos documents — RH, clients, equipe. Les autres pages sont des vues synchronisees de ce cloud.
          </p>
        </div>
        {stats && (
          <div className="hidden md:flex items-center gap-2 text-xs text-slate-400 bg-[#0b0811] border border-white/10 rounded-full px-4 py-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
            {stats.total_documents} documents · {stats.total_folders} dossiers
          </div>
        )}
      </div>

      {!hideScopeTabs && (
        <div className="flex gap-2 mb-6 flex-wrap">
          {scopeTabs.map((t) => {
            const active = activeScope === t.key;
            const count = t.key === "all"
              ? (stats ? stats.total_documents : 0)
              : (stats && stats.by_scope && stats.by_scope[t.key] ? stats.by_scope[t.key].documents : 0);
            const style = active ? { background: t.color } : {};
            const cls = "px-4 py-2 rounded-full text-xs font-bold transition-colors border " +
              (active ? "border-transparent text-[#14101c]" : "bg-[#0b0811] border-white/10 text-slate-400 hover:text-white");
            return (
              <button
                key={t.key}
                onClick={() => { setActiveScope(t.key); setSelectedFolder(null); }}
                data-testid={"cloud-tab-" + t.key}
                className={cls}
                style={style}
              >
                {t.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-12 lg:col-span-3 bg-[#0b0811] border border-white/10 rounded-xl p-4" data-testid="cloud-tree">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Dossiers</div>
            {isAdmin && (
              <button
                onClick={() => openCreateFolder(null)}
                className="text-[#f7c935] hover:bg-[#f7c935]/10 rounded p-1"
                data-testid="cloud-new-folder-btn"
              >
                <FolderPlus className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setSelectedFolder(null)}
            className={"w-full flex items-center gap-1.5 py-1.5 px-2 text-left text-sm mb-1 rounded transition-colors " + (!selectedFolder ? "bg-[#f7c935]/10 text-[#f7c935]" : "text-slate-300 hover:bg-white/5")}
            data-testid="cloud-root-view"
          >
            <Home className="w-4 h-4" />
            <span className="flex-1">Vue globale</span>
          </button>
          {tree.length === 0 ? (
            <div className="text-xs text-slate-500 text-center py-8 italic">Aucun dossier</div>
          ) : (
            <div className="space-y-0.5">
              {flattenTree(tree, expanded).map((item) => (
                <FolderRow
                  key={item.node.folder_id}
                  node={item.node}
                  depth={item.depth}
                  selected={selectedFolder ? selectedFolder.folder_id : null}
                  onSelect={setSelectedFolder}
                  onToggle={toggleExpanded}
                  expanded={expanded}
                />
              ))}
            </div>
          )}
        </aside>

        <main className="col-span-12 lg:col-span-9 bg-[#0b0811] border border-white/10 rounded-xl p-5" data-testid="cloud-main">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm flex-1 min-w-[200px]">
              <Cloud className="w-4 h-4 text-[#f7c935]" />
              {selectedFolder ? (
                <>
                  <span className="text-slate-500">{SCOPE_META[selectedFolder.scope]?.label}</span>
                  <ChevronRight className="w-3 h-3 text-slate-600" />
                  <span className="font-bold text-white">{selectedFolder.name}</span>
                  {isAdmin && (
                    <div className="ml-2 flex gap-1">
                      <button onClick={() => openCreateFolder(selectedFolder)} title="Sous-dossier" className="p-1 rounded hover:bg-white/5">
                        <FolderPlus className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                      <button onClick={() => openEditFolder(selectedFolder)} title="Renommer" className="p-1 rounded hover:bg-white/5">
                        <Pencil className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                      <button onClick={() => deleteFolder(selectedFolder)} title="Supprimer" className="p-1 rounded hover:bg-red-500/10">
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-slate-400">
                  Vue globale — {activeScope === "all" ? "toutes portees" : (SCOPE_META[activeScope]?.label || activeScope)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={inputCls + " pl-9 h-9 w-48"}
                  data-testid="cloud-search"
                />
              </div>
              <Button size="sm" variant="ghost" onClick={() => { loadTree(); loadDocs(); }} className="text-slate-400">
                <RefreshCcw className="w-4 h-4" />
              </Button>
              {isAdmin && (
                <Button
                  onClick={() => setShowUpload(true)}
                  className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]"
                  data-testid="cloud-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Uploader
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#f7c935]" /></div>
          ) : documents.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-white/10 rounded-xl">
              <FileText className="w-12 h-12 mx-auto text-slate-700 mb-3" />
              <p className="text-sm text-slate-500">Aucun document</p>
              {isAdmin && (
                <Button onClick={() => setShowUpload(true)} variant="ghost" className="mt-3 text-[#f7c935]">
                  <Upload className="w-4 h-4 mr-2" /> Importer un premier document
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="cloud-doc-grid">
              {documents.map((d) => {
                const meta = SCOPE_META[d.scope] || SCOPE_META.general;
                const iconBg = { background: meta.color + "20", border: "1px solid " + meta.color + "40" };
                return (
                  <div
                    key={d.doc_id}
                    className="group bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-[#f7c935]/30 transition-colors"
                    data-testid={"cloud-doc-" + d.doc_id}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={iconBg}>
                        <FileText className="w-5 h-5" style={{ color: meta.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-white text-sm truncate">{d.title || d.filename}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                          {d.filename && d.filename !== d.title ? d.filename + " - " : ""}
                          {d.size > 0 ? (d.size / 1024).toFixed(1) + " Ko - " : ""}
                          {new Date(d.uploaded_at).toLocaleDateString("fr-FR")}
                        </div>
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          <ScopeBadge scope={d.scope} />
                          {d.target_user_name && <span className="text-[9px] text-slate-500">{"→ " + d.target_user_name}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-1 mt-3 pt-3 border-t border-white/5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)} className="h-7 text-slate-400 hover:text-white">
                        <Download className="w-3.5 h-3.5 mr-1" /> Ouvrir
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => deleteDoc(d)} className="h-7 text-red-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {showFolderDialog && (
        <FolderDialog
          folder={editingFolder}
          form={folderForm}
          setForm={setFolderForm}
          onClose={() => setShowFolderDialog(false)}
          onSave={saveFolder}
        />
      )}

      {showUpload && (
        <UploadDialog
          onClose={() => setShowUpload(false)}
          onDone={loadDocs}
          api={api}
          team={team}
          defaultScope={activeScope === "all" ? "general" : activeScope}
          defaultFolder={selectedFolder}
        />
      )}
    </div>
  );
}
